import React, { memo, useEffect, useState } from "react";
import { useInvestorProjectsUnit } from "./hooks/useInvestorsProject";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppStore } from "../../../../redux/store";
import { getAuthToken } from "../../../../helpers/authHelper";
import { setFiltersProjectsAction } from "../../../../redux/features/filtersProjects";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import "./scss/ProjectInvestor.scss";

import MainTemplate from "../../layout/MainLayout";
import { Button, Dialog, DialogActions, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { IconChecklist, IconEyeCheck } from "@tabler/icons-react";

const ProjectInvestors = memo(() => {
  const { id = "" } = useParams();
  const [openDocuments, setOpenDocuments] = useState(false);
  const user = useSelector((store: AppStore) => store.auth);
  const token = getAuthToken(user);
  const {
    detailProject,
    loading,
    getDataInvestirProjectBank,
    getDataDetailModel,
    investorBank,
  } = useInvestorProjectsUnit();

  useEffect(() => {
    getDataDetailModel(token, id);
    getDataInvestirProjectBank(token, id);
  }, []);

  const renderStatus = (param: string) => {
    switch (param) {
      case "available":
        return (
          <span className="bg-[#D5F0E3] text-primary px-2 py-1 text-xs font-medium rounded-full">
            Disponible
          </span>
        );
      case "prebooked":
        return (
          <span className="bg-[#FEE3D5] text-orange px-2 py-1 text-xs font-medium rounded-full">
            Pre-reservado
          </span>
        );
      case "taken":
        return (
          <span className="bg-[#DDDEE4] text-blueDark px-2 py-1 text-xs font-medium rounded-full">
            Reservada
          </span>
        );
      case "assigned":
        return (
          <span className="bg-blue700 px-3 py-2 text-xs font-medium text-white rounded-full">
            Asignada
          </span>
        );
      default:
        return "";
    }
  };
  const renderScoreColumn = (params: any) => {
    const score = params.value;
    let scoreColor = "";
    let scoreText = "";

    // Define los colores y el texto dependiendo del valor de score
    if (score >= 90) {
      scoreColor = "green";
      scoreText = "Alto";
    } else if (score >= 70) {
      scoreColor = "yellow";
      scoreText = "Medio";
    } else {
      scoreColor = "red";
      scoreText = "Bajo";
    }

    // Renderiza el termómetro visual con el color y el texto correspondiente
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            backgroundColor: scoreColor,
            marginRight: 5
          }}
        />
        <span>{scoreText}</span>
      </div>
    );
  };
  const handleOpenDialog = () => {
    setOpenDocuments(true);
  };

  const columns: GridColDef[] = [
    {
      field: "investor_name",
      headerName: "Propietario",
      flex: 1,
      headerClassName: "test-colum",
      headerAlign: "center",
      cellClassName: "test-row",
      align: 'center'
    },
    {
      field: "unit_name",
      headerName: "Unidad",
      flex: 1,
      headerClassName: "test-colum",
      headerAlign: "center",
      cellClassName: "test-row",
      align: 'center'
    },
    {
      headerClassName: "test-colum",
      cellClassName: "test-row",
      headerAlign: "center",
      field: "status",
      headerName: "Estado",
      align: 'center',
      flex: 1,
      renderCell: (params) => (
        <div className="w-full">{renderStatus(params.value)}</div>
      ),
    },
    {
      field: "earnings",
      headerName: "Ganancias",
      flex: 1,
      headerClassName: "test-colum",
      headerAlign: "center",
      cellClassName: "test-row",
      align: 'center'
    },
    {
      field: "debt_level",
      headerName: "Debt Level",
      flex: 1,
      headerClassName: "test-colum",
      headerAlign: "center",
      cellClassName: "test-row",
      align: 'center'
    },
    {
      field: "debt",
      headerName: "Debt",
      flex: 1,
      headerClassName: "test-colum",
      headerAlign: "center",
      cellClassName: "test-row",
      align: 'center'
    },
    {
      field: "score",
      headerName: "Score",
      type: "number",
      flex: 1,
      headerClassName: "test-colum",
      headerAlign: "center",
      cellClassName: "test-row",
      align: 'center',
      renderCell: (params) => renderScoreColumn(params)
    },
    {
      field: "document",
      headerName: "Documentos",
      flex: 1,
      headerClassName: "test-colum",
      headerAlign: "center",
      cellClassName: "test-row",
      align: 'center',
      renderCell: (params) => (
        <Button onClick={handleOpenDialog}>
          <IconChecklist color="#212121" />
        </Button>
      )
    },
  ];
  const investorBankWithIds = investorBank.map(
    (investor: any, index: number) => ({
      ...investor,
      id: index + 1, // Utilizando el índice + 1 como ejemplo de identificador único
    })
  );

  const documentsData = [
    { name: "Documento unico de identidad", number: "123", link: "https://example.com/document1" },
    { name: "NIT", number: "456", link: "https://example.com/document2" },
    // Agrega más datos según sea necesario
  ];


  return (
    <MainTemplate>
      <Dialog open={openDocuments} onClose={() => setOpenDocuments(false)}>
        <div className="w-full lg:w-[600px] px-4 py-4">
            <h1 className="font-semibold text-[#212121] text-2xl">Documentación</h1>
            <p className="text-base font-normal text-[#757575]">Detalle de documentos necesarios del propietario</p>
            <Table className="mt-4">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, fontFamily: 'Nunito, sans-serif', fontSize: '16px'}}>Documento</TableCell>
                <TableCell sx={{ fontWeight: 800, fontFamily: 'Nunito, sans-serif', fontSize: '16px'}}>Número de documento</TableCell>
                <TableCell sx={{ fontWeight: 800, fontFamily: 'Nunito, sans-serif', fontSize: '16px'}}>Ver documento</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentsData.map((document, index) => (
                <TableRow key={index}>
                  <TableCell>{document.name}</TableCell>
                  <TableCell>{document.number}</TableCell>
                  <TableCell>
                    <Button component="a" href={document.link} target="_blank" rel="noopener noreferrer"><IconEyeCheck color="#212121" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
      </Dialog>
      <div className="w-full">
        <div className="container mx-auto">
          <div className="py-24">
            <div className=" px-4 py-4 rounded-lg mb-4">
              {!loading && (
                <div className="w-full flex flex-wrap items-center">
                  <div className="w-full lg:w-1/4">
                    <img
                      className=" object-cover object-center h-[200px] w-full rounded-lg"
                      src={detailProject?.cover}
                      alt=""
                    />
                  </div>
                  <div className="w-full lg:w-3/4 md:px-4">
                    <div className="lg:px-4 mt-4 lg:mt-0">
                      <p className=" font-normal text-sm lg:text-base text-[#757575]">
                        #{detailProject?.code}
                      </p>
                      <div className="w-full text-blackGrayScale text-2xl lg:text-3xl font-semibold text-left tracking-wide">
                        {!loading && detailProject?.name}
                      </div>
                      <p className="text-[#757575] text-base md:text-lg">
                        {detailProject?.address}
                      </p>
                    </div>
                    <div className="flex flex-wrap mt-4 gap-4">
                      <div className="py-1 px-4 border border-[#E0E0E0] rounded-full">
                        <p className="text-sm font-bold">Unidades totales</p>
                        <p className="text-xs text-left text-[#757575]">
                          {detailProject?.total_units}
                        </p>
                      </div>
                      <div className="py-1 px-4 border border-[#E0E0E0] rounded-full">
                        <p className="text-sm font-bold">
                          Fecha estimada de entrega
                        </p>
                        <p className="text-xs text-left text-[#757575]">
                          {detailProject?.estimate_built_at}
                        </p>
                      </div>
                      <div className="py-1 px-4 border border-[#E0E0E0] rounded-full">
                        <p className="text-sm font-bold">Disponible</p>
                        <p className="text-xs text-left text-[#757575]">
                          {detailProject?.total_available_units}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="w-full">
                <div className="mb-10 mt-6">
                <h1 className="w-full mt-6 mb-1 text-blackGrayScale text-2xl lg:text-2xl font-semibold text-left tracking-wide">
              Datos de propietarios
            </h1>
            <p className=" text-lg font-medium text-gray600 mb-2">
              Ve la información necesaria sobre los propietarios
            </p>
                </div>
              <div style={{ height: 500, width: "100%" }}>
                <DataGrid
                  rows={investorBankWithIds}
                  columns={columns}
                  loading={loading}
                  autoPageSize
                  getRowId={(row) => row.id} // Definiendo una función para obtener el identificador único de cada fila
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainTemplate>
  );
});

ProjectInvestors.displayName = "ProjectInvestors";
export default ProjectInvestors;
